import React, { useState } from "react";
import { Button } from "react-bootstrap";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ServiceValuesModal, {
  ServiceValueField,
  ServiceValues,
} from "@components/ServiceValuesModal";
import useTheme from "@common/hooks/useTheme";
import useAddSuggestedService, {
  SuggestedServiceDefaults,
} from "../api/useAddSuggestedService";
import useStyles from "../styles/useStyles";
import { MissingServices, MissingServiceSuggestion } from "../types";
import ButtonSpinner from "./ButtonSpinner";

interface Props {
  projectId: string;
  missingServices: MissingServices;
  canManage: boolean;
  canAddServices: boolean;
  dismiss: (taskId: number) => Promise<boolean>;
  undismiss: (taskId: number) => Promise<boolean>;
  onServiceAdded: () => void;
}

interface Toast {
  variant: "success" | "warning";
  text: string;
}

interface Adding extends SuggestedServiceDefaults {
  taskId: number;
}

const SAVE_FAILED = "That change could not be saved.";
const LOAD_FAILED = "That service could not be loaded.";
const HINT = "Services usually sold with what's on this project.";
const NOT_GENERATED =
  "This review was run before missing-service suggestions were available. Refresh the review to check for missing services.";
const INSUFFICIENT = "Suggestions need more project history.";

const PS_FIELDS: ServiceValueField[] = [
  "resource",
  "phase",
  "serviceCategory",
  "quantity",
  "hours",
];
const MS_FIELDS: ServiceValueField[] = PS_FIELDS.filter((f) => f !== "phase");

const insufficientText = ({ basket_count, minimum_baskets }: MissingServices) =>
  basket_count === null || minimum_baskets === null
    ? INSUFFICIENT
    : `${INSUFFICIENT} This account has ${basket_count} of the ${minimum_baskets} past projects needed.`;

const MissingServicesCard: React.FC<Props> = ({
  projectId,
  missingServices,
  canManage,
  canAddServices,
  dismiss,
  undismiss,
  onServiceAdded,
}) => {
  const styles = useStyles();
  const theme = useTheme();
  const { loadDefaults, addService } = useAddSuggestedService(projectId);
  const [toast, setToast] = useState<Toast | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [adding, setAdding] = useState<Adding | null>(null);

  const { status, basket_count, suggestions, dismissed } = missingServices;
  const canAdd = canManage && canAddServices;

  const runChange = async (
    taskId: number,
    change: (id: number) => Promise<boolean>
  ) => {
    setBusyId(taskId);
    const ok = await change(taskId);
    setBusyId(null);
    if (!ok) setToast({ variant: "warning", text: SAVE_FAILED });
  };

  const openAdd = async (suggestion: MissingServiceSuggestion) => {
    setLoadingId(suggestion.task_id);
    try {
      const defaults = await loadDefaults(suggestion.task_id);
      setAdding({ ...defaults, taskId: suggestion.task_id });
    } catch {
      setToast({ variant: "warning", text: LOAD_FAILED });
    } finally {
      setLoadingId(null);
    }
  };

  // Rejections propagate so the dialog shows the message and stays open.
  const applyAdd = async (values: ServiceValues) => {
    if (!adding) return;
    await addService(adding.taskId, adding.serviceType, adding.name, values);
    setAdding(null);
    setToast({
      variant: "success",
      text: `${adding.name} was added to the project.`,
    });
    onServiceAdded();
  };

  const tierColor = {
    high: theme.colors.teal["1"],
    medium: theme.colors.navy["3"],
  };

  const renderBody = () => {
    if (status === "not_generated") return <p>{NOT_GENERATED}</p>;
    if (status === "insufficient_data")
      return <p>{insufficientText(missingServices)}</p>;
    if (suggestions.length === 0) return <p>No missing services found.</p>;
    return suggestions.map((s) => (
      <div key={s.task_id} className={styles.suggestionRow}>
        <div className={styles.suggestionBody}>
          <span className={styles.suggestionName}>{s.name}</span>
          <span
            className={styles.pill}
            style={{ background: tierColor[s.tier] }}
          >
            {s.tier === "high" ? "High" : "Medium"}
          </span>
          {s.because_of.length > 0 && (
            <div className={styles.subline}>
              {`Usually sold with ${s.because_of.join(", ")}`}
            </div>
          )}
        </div>
        <div className={styles.suggestionActions}>
          {canAdd && (
            <Button
              variant="primary"
              className="seafoamBgButton"
              size="sm"
              disabled={loadingId !== null}
              onClick={() => openAdd(s)}
            >
              {loadingId === s.task_id && <ButtonSpinner />}
              Add…
            </Button>
          )}
          {canManage && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busyId === s.task_id}
              onClick={() => runChange(s.task_id, dismiss)}
            >
              {busyId === s.task_id && <ButtonSpinner />}
              Dismiss
            </Button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <div className={styles.card}>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
      <div className={styles.todoHeader}>
        <h2>Missing Services</h2>
      </div>
      <p className={styles.hint}>
        {basket_count !== null && basket_count !== undefined
          ? `${HINT} Based on ${basket_count} past projects.`
          : HINT}
      </p>
      {renderBody()}
      {dismissed.length > 0 && (
        <div>
          <button
            type="button"
            className={styles.linkButton}
            style={{ marginTop: 16 }}
            onClick={() => setShowDismissed((v) => !v)}
          >
            {`${showDismissed ? "▾" : "▸"} Dismissed (${dismissed.length})`}
          </button>
          {showDismissed &&
            dismissed.map((d) => (
              <div key={d.task_id} className={styles.suggestionRow}>
                <div className={styles.suggestionBody}>{d.name}</div>
                {canManage && (
                  <div className={styles.suggestionActions}>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busyId === d.task_id}
                      onClick={() => runChange(d.task_id, undismiss)}
                    >
                      {busyId === d.task_id && <ButtonSpinner />}
                      Undo
                    </Button>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
      {adding && (
        <ServiceValuesModal
          show
          projectId={projectId}
          title={`Add ${adding.name}`}
          applyLabel="Add Service"
          initialValues={adding.values}
          fields={
            adding.serviceType === "managed_services" ? MS_FIELDS : PS_FIELDS
          }
          requiredFields={
            adding.serviceType === "managed_services"
              ? []
              : ["resource", "phase"]
          }
          onApply={applyAdd}
          onClose={() => setAdding(null)}
        />
      )}
    </div>
  );
};

export default MissingServicesCard;
